import { ApmRoute } from '@elastic/apm-rum-react'
import React, { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom"
import { AppState } from '../../../redux/appState'
import { Store } from '../../../redux/store'
import { useLandingPage } from '../../../utils/hooks'
import r from "../../../utils/routes"
import KYCForm from '../Onboarding/KYCForm'
import Layout from './Layout'
import Login from './Login'

const Create = React.lazy(() => import('./Create'))
const CreateDemo = React.lazy(() => import('./Demo'))
const Email = React.lazy(() => import('./Email'))
const Check2FA = React.lazy(() => import('./Check2FA'))
const ViewResetPasswordStep1 = React.lazy(() => import('./ResetPassword/Step1'))
const ViewResetPasswordStep2 = React.lazy(() => import('./ResetPassword/Step2'))
const ViewResetPasswordStep3 = React.lazy(() => import('./ResetPassword/Step3'))
const ViewResetPasswordStep4 = React.lazy(() => import('./ResetPassword/Step4'))
const UploadDocs = React.lazy(() => import('./UploadDocs'))
const Deposit = React.lazy(() => import('./Deposit'))
const Congrats = React.lazy(() => import('./Congrats'))
const AccountCreatedLevelOne = React.lazy(() => import('../Onboarding/SimplifiedFlow/AccountCreatedLevelOne'))
const PersonalInformation = React.lazy(() => import('../Onboarding/PersonalInformation'))
const SimplifiedContactAndAddress = React.lazy(() => import('./SimplifiedContactAndAddress'))
const SimplifiedAccountPendingApproval = React.lazy(() => import('./SimplifiedAccountPendingApproval'))
const PAMMQuestionnaire = React.lazy(() => import('./PAMMQuestionnaire'))
const IBPhone = React.lazy(() => import('ib/onboarding/Phone'))
const IBQ1 = React.lazy(() => import('ib/onboarding/Q1'))
const IBQ2 = React.lazy(() => import('ib/onboarding/Q2'))
const IBQ3 = React.lazy(() => import('ib/onboarding/Q3'))
const IBQ4 = React.lazy(() => import('ib/onboarding/Q4'))
const IBLegal = React.lazy(() => import('ib/onboarding/Legal'))
const IBAddress = React.lazy(() => import('ib/onboarding/Address'))
const IBAccountCreated = React.lazy(() => import('ib/onboarding/AccountCreated'))
const IBPendingApproval = React.lazy(() => import('ib/onboarding/PendingApproval'))
const IBUploadDocs = React.lazy(() => import('ib/onboarding/UploadDocs'))
const SocialLoginCallback = React.lazy(() => import('../Onboarding/SocialLoginCallback'))
const SocialRegistration = React.lazy(() => import('../Onboarding/SocialRegistration'))

interface AccountProps extends RouteComponentProps { }

// THPRT-6269: the legacy application step machine is retired — every application
// (individual and corporate) continues in the General/Simplified flow at /onboarding.
// These paths stay mounted only to catch bookmarks/back-button and redirect.
const LEGACY_APPLICATION_STEP_PATHS = [
       r.account.contactAndAddress,
       r.account.phoneNumber,
       r.account.smsVerification,
       r.account.selectPlatform,
       r.account.financialDetails,
       r.account.financialDetailsSecondPage,
       r.account.clientJourney,
       r.account.accountType,
       r.account.experience,
       r.account.consent,
       r.account.appropriatenessTest,
       r.account.chessSponsorship,
       r.account.usaCitizenship,
       r.account.foreignExchangeBusiness,
       r.account.pepLegalConsent,
       r.account.forexConfirmation,
       r.account.traderClassification,
       r.account.corporate.corporateInformation,
       r.account.corporate.contacts,
       r.account.corporate.address,
       r.account.corporate.placeOfBusiness,
       r.account.corporate.regulation,
       r.account.corporate.representative,
       r.account.corporate.directors,
       r.account.corporate.shareholders,
       r.account.corporate.financialDetails,
       r.account.corporate.experience,
       r.account.corporate.appropriatenessTest,
       r.account.corporate.forexConfirmation,
       r.account.corporate.selectPlatform,
       r.account.corporate.chessSponsorship,
]

const Account = (_: AccountProps) => {
       const store: AppState = useSelector(
              (state: Store) => state.App)

       const { loggedIn } = store
       const landingPage = useLandingPage()

       const redirectUrl = useCallback(() => {
              if (loggedIn) {
                     return landingPage
              }
              else return r.account.login
       }, [landingPage, loggedIn])

       return useMemo(() => {
              const to = (path: string) => {
                     return {
                            path,
                            key: path
                     }
              }
              return <Switch>
                     <Route exact {...to(r.register.ib)}
                            render={props => (<Create {...props} />)} />
                     <Route exact {...to(r.register.demo)}>
                            <Redirect to={r.account.createAccount.demo} />
                     </Route>
                     <Route exact path={[r.register.root, r.register.live]}>
                            <Redirect to={r.account.createAccount.live} />
                     </Route>
                     <Route exact {...to(r.welcome.demo)}>
                            <Redirect to={r.account.createAccount.demo} />
                     </Route>
                     <Route exact {...to(r.account.createAccount.demo)}
                            component={CreateDemo} />
                     <Route exact {...to(r.account.callbackDemo)}
                            render={() => <SocialLoginCallback demo />} />
                     <Route exact {...to(r.account.callback)}
                            component={SocialLoginCallback} />
                     <Route exact {...to(r.account.socialRegistration)}
                            component={SocialRegistration} />
                     <ApmRoute exact {...to(r.account.login)}
                            component={Login} />
                     <Route {...to(r.account.createAccount.live)}
                            render={props => (<Create {...props} />)} />
                     <Route path={LEGACY_APPLICATION_STEP_PATHS}
                            key="legacy-application-steps">
                            <Redirect to={r.onboarding} />
                     </Route>
                     <ApmRoute {...to(r.account.resetPasswordStep1)}
                            component={ViewResetPasswordStep1} />
                     <ApmRoute {...to(r.account.resetPasswordStep2)}
                            component={ViewResetPasswordStep2} />
                     <ApmRoute {...to(r.account.resetPasswordStep3)}
                            component={ViewResetPasswordStep3} />
                     <ApmRoute {...to(r.account.resetPasswordStep4)}
                            component={ViewResetPasswordStep4} />
                     {/* Application process */}
                     <Route {...to(r.account.personalInformation)}
                            render={_ => (<Layout><PersonalInformation /></Layout>)} />
                     <ApmRoute {...to(r.account.check2fa)}
                            component={Check2FA} />
                     <ApmRoute {...to(r.account.validateEmail)}
                            component={Email} />,
                     <Route {...to(r.account.pammQuestionnaire)}
                            component={() => (<Layout><PAMMQuestionnaire /></Layout>)} />
                     <Route {...to(r.account.verify.root)}
                            component={() => (<KYCForm><UploadDocs /></KYCForm>)} />
                     <Route {...to(r.account.deposit)}
                            render={props => <Deposit {...props} />} />,
                     <Route {...to(r.account.congrats)}
                            component={() => (<Layout><Congrats /></Layout>)} />
                     <Route {...to(r.account.simplified.accountCreated)}
                            component={() => (<Layout><AccountCreatedLevelOne /></Layout>)} />
                     <Route {...to(r.account.simplified.address)}
                            component={() => (<Layout><SimplifiedContactAndAddress /></Layout>)} />
                     <Route {...to(r.account.simplified.pendingApproval)}
                            component={() => (<Layout><SimplifiedAccountPendingApproval /></Layout>)} />
                     <Route {...to(r.account.ib.phone)}
                            component={() => (<Layout><IBPhone /></Layout>)} />
                     <Route {...to(r.account.ib.q1)}
                            component={() => (<Layout><IBQ1 /></Layout>)} />
                     <Route {...to(r.account.ib.q2)}
                            component={() => (<Layout><IBQ2 /></Layout>)} />
                     <Route {...to(r.account.ib.q3)}
                            component={() => (<Layout><IBQ3 /></Layout>)} />
                     <Route {...to(r.account.ib.q4)}
                            component={() => (<Layout><IBQ4 /></Layout>)} />
                     <Route {...to(r.account.ib.legal)}
                            component={() => (<Layout><IBLegal /></Layout>)} />
                     <Route {...to(r.account.ib.address)}
                            component={() => (<Layout><IBAddress /></Layout>)} />
                     <Route {...to(r.account.ib.accountCreated)}
                            component={() => (<Layout><IBAccountCreated /></Layout>)} />
                     <Route {...to(r.account.ib.pendingApproval)}
                            component={() => (<Layout><IBPendingApproval /></Layout>)} />
                     <Route path={[r.account.ib.verify.address, r.account.ib.verify.id]}
                            component={() => (<Layout><IBUploadDocs /></Layout>)} />
                     <Redirect to={redirectUrl()} />
              </Switch >
       }, [redirectUrl])

}

export default memo(Account)